import React, { useState } from 'react';
import { Star, Send, CheckCircle2, Sparkles, MessageSquare } from 'lucide-react';
import { toast } from './Toast';

interface BetaFeedbackCardProps {
  jobId: string;
  originalDurationSeconds: number;
  cutDurationSeconds: number;
  userEmail?: string;
}

export const BetaFeedbackCard: React.FC<BetaFeedbackCardProps> = ({
  jobId,
  originalDurationSeconds,
  cutDurationSeconds,
  userEmail
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const tags = [
    'Cortes limpios y fluidos',
    'Cortó alguna frase a medias',
    'El resumen quedó muy corto',
    'El resumen quedó muy largo',
    'Faltaron momentos clave'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      toast.warning('Puntúa tu experiencia', 'Por favor selecciona de 1 a 5 estrellas para valorar el resumen.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId,
          rating,
          tag: selectedTag || 'Sin etiqueta',
          comment: comment.trim(),
          originalDurationSeconds,
          cutDurationSeconds,
          userEmail: userEmail || 'creador_beta@google.com',
          timestamp: Date.now()
        })
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success('¡Feedback registrado!', 'Tu valoración ayudará a calibrar el siguiente modelo de IA.');
      } else {
        throw new Error('Error en el servidor');
      }
    } catch {
      // Local fallback
      setSubmitted(true);
      toast.success('¡Feedback registrado!', 'Gracias por participar en la beta de entrenamiento.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 text-center space-y-2">
        <div className="w-9 h-9 rounded-full bg-zinc-800 text-emerald-400 flex items-center justify-center mx-auto mb-1">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <h4 className="text-sm font-semibold text-white">¡Gracias por entrenar la IA!</h4>
        <p className="text-xs text-zinc-400 max-w-sm mx-auto">
          Tus notas y valoración sobre el ritmo de cortes han quedado registradas para calibrar los próximos modelos.
        </p>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 text-left space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-zinc-300" />
            <h4 className="text-sm font-semibold text-white">Entrena la IA de la Beta</h4>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-800 text-zinc-300 border border-zinc-700">
              1 de 100 vídeos
            </span>
          </div>
          <p className="text-xs text-zinc-400">
            ¿Qué tal fue la selección editorial de momentos y la fluidez del audio?
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 pt-1">
        
        {/* Star Rating */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400 mr-2">Valoración general:</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                type="button"
                key={star}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
                className="p-1 text-zinc-600 hover:text-amber-400 transition-colors"
                title={`${star} estrellas`}
              >
                <Star
                  className={`w-5 h-5 transition-transform ${
                    (hoverRating || rating) >= star
                      ? 'fill-amber-400 text-amber-400 scale-110'
                      : 'text-zinc-600'
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <span className="text-xs text-amber-400 font-medium ml-1">
              {rating === 5 ? '¡Excelente!' : rating === 4 ? 'Muy bueno' : rating === 3 ? 'Aceptable' : 'A mejorar'}
            </span>
          )}
        </div>

        {/* Quick Tags */}
        <div className="space-y-1.5">
          <label className="text-[11px] text-zinc-400 block">¿Cómo percibiste los cortes de voz?</label>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                type="button"
                key={tag}
                onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${
                  selectedTag === tag
                    ? 'bg-zinc-200 text-zinc-950 border-zinc-200 font-medium'
                    : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Optional Comment */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-400">
            <MessageSquare className="w-3 h-3" />
            <span>Sugerencias para el editor de IA (opcional):</span>
          </div>
          <textarea
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ej: En el minuto 2 cortó demasiado rápido, o me gustaría que el gancho dure 10 segundos más..."
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-600 rounded-lg p-2.5 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none resize-none transition-colors"
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary py-2 px-4 text-xs font-medium flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5" />
            <span>{isSubmitting ? 'Guardando...' : 'Enviar feedback de entrenamiento'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};
